import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import {
  Checkbox,
  Icon,
  Input,
  Modal,
  Switch,
  Textarea,
  Tooltip,
} from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';

import { sharePatientFormSchema } from '../../config/schema';
import { SharePatientFormPayloadType } from '../../config/types';
import { useInviteForSharingPatient } from '../../hooks/useInviteForSharingPatient';
import { ProtectedSharingModal } from '../ProtectedSharingModal/ProtectedSharingModal';

import styles from './SharePatientModalForm.module.scss';

export const SharePatientModalForm: FC = () => {
  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();

  const [isProtectedSharing, setIsProtectedSharing] = useState(false);

  const {
    visible,
    data: { patientID },
  } = useAppSelector((state) => state.modal.SharePatient);

  const isForbidSharing = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_Sharing,
    ),
  );

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { isSubmitting },
  } = useForm<SharePatientFormPayloadType>({
    resolver: yupResolver(sharePatientFormSchema),
    mode: 'all',
  });

  const inviteForSharingPatient = useInviteForSharingPatient();

  const handleClose = () => {
    reset();

    dispatch(modalModel.actions.closeModal(ModalID.SharePatient));
  };

  const onSubmit: SubmitHandler<SharePatientFormPayloadType> = async (data) => {
    try {
      await inviteForSharingPatient({
        patientID,
        sharedPatientDoctorEmail: data.email,
        comment: data?.comment ?? '',
        isProtectedSharing,
      });
    } catch (error) {
      // TODO: [1/l]  Investigate reason of this behaviour
      // We need a catch for useForm's isSubmitting to work correctly
    } finally {
      handleClose();
    }
  };

  if (isForbidSharing) {
    return null;
  }

  return (
    <>
      <Modal
        data-component="share-patient-modal"
        title={
          <FormattedMessage
            id="sharePatient.title"
            defaultMessage="Share patient case"
          />
        }
        isOpen={visible}
        onCancel={handleClose}
        className={styles.modal}
        bodyClassName={styles.modalBody}
        containerClassName={styles.modalContainer}
        okButtonText={formatMessage({
          id: 'sharePatient.shareCase',
          defaultMessage: 'Share case',
        })}
        okButtonProps={{
          type: 'submit',
          form: 'sharePatientForm',
          loading: isSubmitting,
        }}
      >
        <div className={styles.alertInfo}>
          <Icon name="alert" size={24} className={styles.alertIcon} />

          <span className="p2">
            <FormattedMessage
              id="sharePatient.alertInfoMedical"
              defaultMessage="You can only share patient information with medical professionals,
          which will have access to the patient's studies and reports.Enter the
          doctor's email"
            />
          </span>
        </div>

        <form
          id="sharePatientForm"
          onSubmit={handleSubmit(onSubmit)}
          className={styles.sharePatientForm}
        >
          <Controller
            control={control}
            name="email"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error, isTouched },
            }) => (
              <Input
                required
                ref={ref}
                value={value}
                name={name}
                inputMode="email"
                type="email"
                onBlur={onBlur}
                onChange={(event) => {
                  if (!isTouched) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                label={formatMessage({
                  id: 'global.email',
                  defaultMessage: 'Email',
                })}
                error={error?.message}
                autoComplete="email"
              />
            )}
          />

          <Controller
            control={control}
            name="comment"
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Textarea
                value={value ?? ''}
                placeholder={formatMessage({
                  id: 'sharePatient.leaveComment',
                  defaultMessage: 'Leave a comment for the doctor...',
                })}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
              />
            )}
          />

          <Switch
            checked={isProtectedSharing}
            onChange={() => setIsProtectedSharing(!isProtectedSharing)}
            className={styles.protectionSwitch}
            labelClassName={styles.protectionSwitchLabel}
          >
            <FormattedMessage
              id="sharePatient.withProtection"
              defaultMessage="Protect patient data sharing with password"
            />

            <Tooltip.Primary
              content={formatMessage({
                id: 'sharePatient.withProtectionTooltip',
                defaultMessage:
                  'Generate secure password for this patient’s shared case. Shared access will require the recipient to enter this password.',
              })}
            >
              <Icon
                name="info"
                size={24}
                className={styles.protectionSwitchIcon}
              />
            </Tooltip.Primary>
          </Switch>

          <Controller
            control={control}
            name="patientCaseSharingConfirmation"
            render={({
              field: { value = false, onChange, name },
              fieldState: { error },
            }) => (
              <Checkbox
                checked={value}
                onChange={onChange}
                name={name}
                error={error?.message}
                required
              >
                <span className={styles.text}>
                  <FormattedMessage
                    id="sharePatient.patientCaseSharingConfirmationMedical"
                    defaultMessage="I confirm that I share patient case to a medical professional"
                  />
                </span>
              </Checkbox>
            )}
          />

          <Controller
            control={control}
            name="patientDataSharingConsentConfirmation"
            render={({
              field: { value = false, onChange, name },
              fieldState: { error },
            }) => (
              <Checkbox
                checked={value}
                onChange={onChange}
                name={name}
                error={error?.message}
                required
              >
                <span className={styles.text}>
                  <FormattedMessage
                    id="sharePatient.patientDataSharingConsentConfirmationMedical"
                    defaultMessage="I confirm that I received permission from the patient to share patient's personal data in Diagnocat to the medical professional specified above"
                  />
                </span>
              </Checkbox>
            )}
          />
        </form>
      </Modal>

      <ProtectedSharingModal />
    </>
  );
};
